import React, { useEffect, useState } from "react";
import { Activity, RefreshCw, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Phase 27c · Queue monitor tab — status of all 5 video queues.
 */
export default function AdminVideosQueueMonitor() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/videos/v2/queue-status/all`, {
        credentials: "include",
      });
      if (r.ok) setStatus(await r.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const int = setInterval(fetchStatus, 30000);
    return () => clearInterval(int);
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-queue-monitor">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold inline-flex items-center gap-2">
          <Activity className="h-4 w-4" /> Queue Monitor · Live (30s poll)
        </h2>
        <button
          data-testid="admin-queue-refresh"
          onClick={fetchStatus}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-full border border-[var(--color-border)] hover:border-[var(--color-primary)] inline-flex items-center gap-1 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </button>
      </div>
      {loading && !status ? (
        <div className="flex items-center justify-center py-12 text-text-secondary text-xs">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="queue-status-grid">
          {status && Object.entries(status.queues || {}).map(([qName, qData]) => (
            <div key={qName} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 space-y-2"
              data-testid={`queue-card-${qName}`}>
              <div className="text-[10px] uppercase tracking-widest text-text-secondary">
                {qName.toUpperCase()} queue
              </div>
              {qData.error ? (
                <div className="text-xs text-[var(--color-alert)]">Error: {qData.error}</div>
              ) : (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-text-secondary">Queued</div>
                    <div className="text-lg font-semibold text-[var(--color-primary)]">{qData.counts?.queued || 0}</div>
                  </div>
                  <div>
                    <div className="text-text-secondary">In progress</div>
                    <div className="text-lg font-semibold text-[var(--color-secondary)]">{qData.counts?.in_progress || 0}</div>
                  </div>
                  <div>
                    <div className="text-text-secondary">Completed</div>
                    <div className="text-lg font-semibold text-[var(--color-success)]">{qData.counts?.completed || 0}</div>
                  </div>
                  <div>
                    <div className="text-text-secondary">Failed</div>
                    <div className="text-lg font-semibold text-[var(--color-alert)]">{qData.counts?.failed || 0}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-text-secondary/70">
        Checked at: {status?.checked_at || "—"}
      </div>
    </div>
  );
}
